<template>
    <modal
        name="titleease-order-modal"
        :adaptive="true"
        :width="560"
        height="auto"
        :styles="{ boxShadow: 'none', background: 'transparent' }"
        @before-close="handleBeforeClose"
    >
        <div class="rounded-lg bg-white p-8 sm:p-10"
            @click.stop
        >
            <template v-if="step === 'submitting'">
                <div class="flex py-10 text-xl justify-center items-center text-blue-600">
                    <spinner class="w-8 h-8 mr-4 flex-none" />
                    <span>Sending information to TitleEase ...</span>
                </div>
            </template>

            <template v-else-if="step === 'success'">
                <h2 class="text-xl sm:text-2xl text-blue-700 font-semibold pb-6">
                    Title Order Submitted
                </h2>

                <p class="text-md text-gray-600 leading-relaxed">
                    A title order has been successfully submitted for this transaction.
                    An invitation to access your TitleEase order will be sent to your email
                    within 24–48 hours.
                </p>

                <div class="flex items-center justify-end pt-6">
                    <button-blue @click="done">
                        Done
                    </button-blue>
                </div>
            </template>

            <template v-else-if="step === 'disclaimer'">
                <h2 class="text-xl sm:text-2xl text-blue-700 font-semibold pb-6">
                    Please Confirm
                </h2>

                <div class="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                        I/we understand that Contract2Close.com has a business relationship with TitleEase, LLC and may refer me/us to TitleEase for settlement services. Because of this relationship, Contract2Close.Com, TitleEase, or their affiliates may receive a financial or other benefit.
                    </p>
                    <p>
                        I/we understand that I/we are <strong>not required</strong> to use TitleEase, LLC and may choose any other settlement service provider. There are frequently other providers available, and I/we are free to shop around.
                    </p>
                </div>

                <p v-if="error"
                    class="mt-4 text-sm text-red-600 leading-snug"
                >
                    {{ error }}
                </p>

                <div class="flex items-center justify-between pt-6">
                    <button-gray-outline
                        :disabled="submitting"
                        @click="step = 'confirm'"
                    >
                        Back
                    </button-gray-outline>

                    <button-blue
                        :disabled="submitting || read_only"
                        @click="submitOrder"
                    >
                        Continue
                    </button-blue>
                </div>
            </template>

            <template v-else>
                <h2 class="text-xl sm:text-2xl text-blue-700 font-semibold pb-6">
                    Order Title with TitleEase
                </h2>

                <div class="space-y-4 text-gray-600 leading-relaxed">
                    <p>
                        TitleEase provides title insurance and closing services for your transaction.
                        When you continue, we will securely send your property and transaction details
                        to TitleEase so they can begin processing your title order.
                    </p>

                    <p class="font-semibold text-gray-warm-800">
                        Information shared with TitleEase:
                    </p>

                    <ul class="list-disc pl-5 space-y-1 text-sm">
                        <li v-if="formattedAddress">Property address: {{ formattedAddress }}</li>
                        <li v-if="property.purchase_price">Purchase price: {{ formattedPurchasePrice }}</li>
                        <li v-if="formattedClosingDate">
                            Closing date: {{ formattedClosingDate }}
                        </li>
                        <li>Transaction type: {{ transactionType }}</li>
                        <li v-if="include_contract === 'yes' && selectedContractDocument">
                            Signed contract: {{ selectedContractDocument.name }}
                        </li>
                    </ul>

                    <div v-if="contract_documents.length"
                        class="border border-gray-200 rounded-md bg-gray-50 p-4 space-y-3"
                    >
                        <p class="font-semibold text-gray-warm-800 mb-0">
                            Do you have a signed contract to include with this order?
                        </p>

                        <div class="flex items-center gap-6 text-sm">
                            <label class="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="include_contract"
                                    value="yes"
                                    v-model="include_contract"
                                >
                                <span>Yes</span>
                            </label>

                            <label class="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="include_contract"
                                    value="no"
                                    v-model="include_contract"
                                >
                                <span>No</span>
                            </label>
                        </div>

                        <div v-if="include_contract === 'yes'">
                            <label class="block text-sm text-gray-600 mb-1">
                                Select the signed contract
                            </label>

                            <select
                                v-model="selected_contract_key"
                                class="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                            >
                                <option value="">
                                    Choose a document...
                                </option>
                                <option
                                    v-for="document in contract_documents"
                                    :key="documentKey(document)"
                                    :value="documentKey(document)"
                                >
                                    {{ document.name }}
                                    <template v-if="document.source === 'docusign'"> (Docusign)</template>
                                </option>
                            </select>

                            <p v-if="contract_documents[0]?.source === 'docusign'"
                                class="text-xs text-gray-500 mt-2 mb-0"
                            >
                                Showing completed Docusign envelopes. The signed PDF will be retrieved
                                from Docusign and included with your order.
                            </p>
                        </div>
                    </div>

                    <p class="text-sm">
                        You will receive confirmation once TitleEase accepts the order.
                        A TitleEase representative may follow up if additional information is needed.
                    </p>
                </div>

                <p v-if="error"
                    class="mt-4 text-sm text-red-600 leading-snug"
                >
                    {{ error }}
                </p>

                <div class="flex items-center justify-between pt-6">
                    <button-gray-outline
                        :disabled="submitting"
                        @click="close"
                    >
                        Cancel
                    </button-gray-outline>

                    <button-blue
                        :disabled="submitting || read_only || ! canSubmit"
                        @click="showDisclaimer"
                    >
                        Order Title
                    </button-blue>
                </div>
            </template>
        </div>
    </modal>
</template>

<script>
import ButtonBlue from '../../components/button-blue.vue';
import ButtonGrayOutline from '../../components/button-gray-outline.vue';
import Spinner from '../../components/spinner.vue';

const MIN_PROGRESS_MS = 5000;

export default {
    components: {
        ButtonBlue,
        ButtonGrayOutline,
        Spinner,
    },

    props: {
        show: {},
        property: { default: () => ({}) },
        order_title_route: {},
        contract_documents_route: {},
        read_only: { default: false },
    },

    data() {
        return {
            step: 'confirm',
            submitting: false,
            error: null,
            orderResult: null,
            contract_documents: [],
            loading_documents: false,
            include_contract: 'no',
            selected_contract_key: '',
        }
    },

    computed: {
        selectedContractDocument() {
            if (! this.selected_contract_key) {
                return null;
            }

            return this.contract_documents.find(
                document => this.documentKey(document) === this.selected_contract_key
            ) || null;
        },
        canSubmit() {
            if (! this.contract_documents.length || this.include_contract !== 'yes') {
                return true;
            }

            return !! this.selectedContractDocument;
        },
        formattedAddress() {
            return [
                this.property.address,
                this.property.address_2,
                [this.property.city, this.property.state].filter(Boolean).join(', '),
                this.property.zip,
            ].filter(Boolean).join(', ');
        },
        formattedPurchasePrice() {
            if (! this.property.purchase_price) {
                return null;
            }

            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.property.currency || 'USD',
            }).format(this.property.purchase_price);
        },
        formattedClosingDate() {
            const date = this.property.closing?.scheduled_closing_date;

            if (! date) {
                return null;
            }

            return dayjs(String(date).slice(0, 10)).format('M/D/YYYY');
        },
        transactionType() {
            if (this.property.deal?.our_purchase) {
                return 'Purchase';
            }

            if (this.property.deal?.our_sale) {
                return 'Sale';
            }

            return 'Transaction';
        },
    },

    methods: {
        documentKey(document) {
            return `${document.type}:${document.id}`;
        },
        resetState() {
            this.step = 'confirm';
            this.submitting = false;
            this.error = null;
            this.orderResult = null;
            this.include_contract = 'no';
            this.selected_contract_key = '';
        },
        showDisclaimer() {
            if (this.read_only || this.submitting || ! this.canSubmit) {
                return;
            }

            this.error = null;
            this.step = 'disclaimer';
        },
        close() {
            if (this.step === 'submitting') {
                return;
            }

            this.$emit('closed');
        },
        done() {
            if (this.orderResult) {
                this.$emit('ordered', this.orderResult);
            }

            this.$emit('closed');
        },
        handleBeforeClose(event) {
            if (this.step === 'submitting') {
                event.stop();
                return;
            }

            if (! event.stop) {
                if (this.step === 'success' && this.orderResult) {
                    this.$emit('ordered', this.orderResult);
                }

                this.$emit('closed');
            }
        },
        wait(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        loadContractDocuments() {
            if (! this.contract_documents_route || this.loading_documents) {
                return;
            }

            this.loading_documents = true;

            axios.get(this.contract_documents_route)
                .then(response => {
                    this.contract_documents = response.data.documents || [];
                }).catch(() => {
                    this.contract_documents = [];
                }).finally(() => {
                    this.loading_documents = false;
                });
        },
        async submitOrder() {
            if (this.read_only || this.submitting || ! this.canSubmit) {
                return;
            }

            this.submitting = true;
            this.error = null;
            this.step = 'submitting';

            const payload = {};

            if (this.include_contract === 'yes' && this.selectedContractDocument) {
                payload.contract_document_type = this.selectedContractDocument.type;
                payload.contract_document_id = this.selectedContractDocument.id;
            }

            const minDelay = this.wait(MIN_PROGRESS_MS);
            const request = axios.post(this.order_title_route, payload)
                .then(response => ({ ok: true, response }))
                .catch(error => ({ ok: false, error }));

            const [result] = await Promise.all([request, minDelay]);

            if (! result.ok) {
                this.error = result.error.response?.data?.error
                    || 'Unable to submit your title order. Please try again.';
                this.step = 'disclaimer';
                this.submitting = false;
                return;
            }

            this.orderResult = result.response.data;
            this.step = 'success';
            this.submitting = false;
        },
    },

    watch: {
        show: {
            handler(value) {
                if (value) {
                    this.resetState();
                    this.loadContractDocuments();

                    this.$nextTick(() => {
                        this.$modal.show('titleease-order-modal');
                    });
                } else {
                    this.$modal.hide('titleease-order-modal');
                }
            },
            immediate: true,
        },
        include_contract(value) {
            if (value !== 'yes') {
                this.selected_contract_key = '';
            }
        },
    },
}
</script>

<style scoped>
.vm--overlay {
    background: #000000;
    opacity: 0.7;
}
</style>
